/**
 * Cards Carousel: staggered appear (translateY + opacity) via Motion.
 * Trigger: Motion inView(section, …, { amount: "some" }) — motion.dev/docs/inview
 *
 * This is an In view reveal, so its ease/duration/tier-delay follow the global
 * Theme default (Settings › Animations › In view), shared with
 * 288-stagger-appear.js via window.__288Stagger. The stagger order is NOT linear:
 * it radiates out from the active/centre slide (tier = |index − activeIndex|).
 * Opacity always fades linearly (motion-token rule); only the rise gets the curve.
 */
const SECTION_SELECTOR = '.cards-carousel[data-appear-animation="true"]';
const SPLIDE_SELECTOR = '.cards-carousel__splide';

/** @type {Record<string, number[] | "linear">} */
const EASE_PRESETS = {
  'expo-out': [0.19, 1, 0.22, 1],
  'cubic-out': [0.215, 0.61, 0.355, 1],
  'quad-out': [0.25, 0.46, 0.45, 0.94],
  'sine-out': [0.39, 0.575, 0.565, 1],
  linear: 'linear',
};

/** In view appear config from the global Theme default (window.__288Stagger). */
function readInViewConfig() {
  const cfg = (typeof window !== 'undefined' && window.__288Stagger) || {};
  /** @param {unknown} v @param {number} fb */
  const num = (v, fb) => {
    const n = parseFloat(String(v));
    return Number.isFinite(n) && n >= 0 ? n : fb;
  };
  const easeKey =
    typeof cfg.ease === 'string' && EASE_PRESETS[cfg.ease] ? cfg.ease : 'expo-out';
  return {
    easeInView: EASE_PRESETS[easeKey],
    durationS: num(cfg.duration, 500) / 1000,
    opacityDurationS: num(cfg.opacityDuration, 700) / 1000,
    // Tier step = the In view "delay between items".
    tierDelayS: num(cfg.delay, 160) / 1000,
  };
}
const APPEAR = readInViewConfig();
const EASE_IN_VIEW = APPEAR.easeInView;
/** Opacity always fades linearly. */
const EASE_OPACITY = 'linear';
const DURATION_S = APPEAR.durationS;
const OPACITY_DURATION_S = APPEAR.opacityDurationS;
const TIER_DELAY_S = APPEAR.tierDelayS;
const MIN_TRACK_OVERLAP_PX = 0.5;

/**
 * Per-section appear config: the global In view default merged with this
 * carousel's own data-appear-* overrides (emitted by snippets/288-appear.liquid
 * with overrides_only). A missing/invalid attribute inherits the global.
 * @param {HTMLElement} section
 */
function getSectionAppear(section) {
  const d = section.dataset;
  /** @param {string|undefined} v @param {number} fb */
  const ms = (v, fb) => {
    const n = parseFloat(String(v));
    return Number.isFinite(n) && n >= 0 ? n / 1000 : fb;
  };
  /** @param {string|undefined} v @param {number[]|"linear"} fb */
  const ease = (v, fb) => (v && EASE_PRESETS[v] ? EASE_PRESETS[v] : fb);
  return {
    easeInView: ease(d.appearEase, EASE_IN_VIEW),
    durationS: ms(d.appearDuration, DURATION_S),
    opacityDurationS: ms(d.appearOpacityDuration, OPACITY_DURATION_S),
    tierDelayS: ms(d.appearDelay, TIER_DELAY_S),
  };
}

/** @type {WeakMap<HTMLElement, () => void>} */
const cardsCarouselAppearCleanups = new WeakMap();

/** @param {() => void} fn */
function whenReady(fn) {
  if (document.readyState !== 'loading') {
    fn();
  } else {
    document.addEventListener('DOMContentLoaded', fn);
  }
}

function prefersReducedMotion() {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/**
 * Horizontal overlap width between two DOMRects (0 if none).
 * @param {DOMRectReadOnly} a
 * @param {DOMRectReadOnly} b
 */
function horizontalOverlapWidth(a, b) {
  return Math.min(a.right, b.right) - Math.max(a.left, b.left);
}

/**
 * Slides that overlap the track horizontally (includes partially clipped edge cards).
 * Do not use Splide's `.is-visible` alone — it often omits peeking slides at the ends.
 *
 * @param {HTMLElement} splideEl
 * @returns {HTMLElement[]}
 */
function getVisibleSlides(splideEl) {
  const track = splideEl.querySelector('.splide__track');
  if (!track) return [];
  const tr = track.getBoundingClientRect();
  const slides = [...splideEl.querySelectorAll('.splide__list > .splide__slide')].filter(
    (el) => el instanceof HTMLElement
  );
  const visible = slides.filter((slide) => {
    const r = slide.getBoundingClientRect();
    if (r.height < 1) return false;
    return horizontalOverlapWidth(r, tr) > MIN_TRACK_OVERLAP_PX;
  });
  visible.sort(
    (a, b) => a.getBoundingClientRect().left - b.getBoundingClientRect().left
  );
  return visible;
}

/**
 * @param {HTMLElement} splideEl
 * @param {number} maxMs
 * @returns {Promise<boolean>}
 */
function waitForSplideReady(splideEl, maxMs = 3000) {
  return new Promise((resolve) => {
    const start = performance.now();
    function tick() {
      if (splideEl.classList.contains('is-initialized')) {
        resolve(true);
        return;
      }
      if (performance.now() - start > maxMs) {
        resolve(false);
        return;
      }
      requestAnimationFrame(tick);
    }
    tick();
  });
}

/**
 * Spotlight carousel locks track height (images/fonts) before appear; spotlight
 * height transitions stay off until appear finishes (trackHeightReady).
 *
 * @param {HTMLElement} section
 * @param {number} maxMs
 * @returns {Promise<void>}
 */
function waitForTrackHeightLocked(section, maxMs = 4000) {
  if (section.dataset.spotlight !== 'true') {
    return Promise.resolve();
  }

  return new Promise((resolve) => {
    if (section.dataset.trackHeightLocked === 'true') {
      resolve();
      return;
    }

    const start = performance.now();
    function tick() {
      if (
        section.dataset.trackHeightLocked === 'true' ||
        performance.now() - start > maxMs
      ) {
        resolve();
        return;
      }
      requestAnimationFrame(tick);
    }
    tick();
  });
}

/**
 * @param {HTMLElement} splideEl
 */
function revealSlidesStatic(splideEl) {
  splideEl.querySelectorAll('.splide__slide').forEach((el) => {
    if (el instanceof HTMLElement) {
      el.style.opacity = '1';
      el.style.transform = 'none';
    }
  });
}

/**
 * @param {HTMLElement} section
 */
function notifyAppearComplete(section) {
  section.dispatchEvent(
    new CustomEvent('cards-carousel:appear-complete', { bubbles: false })
  );
}

/**
 * @param {HTMLElement} splideEl
 * @param {(target: Element, keyframes: object, options?: object) => unknown} animate
 */
async function runCarouselAppear(section, splideEl, animate) {
  await waitForTrackHeightLocked(section);

  if (prefersReducedMotion()) {
    revealSlidesStatic(splideEl);
    notifyAppearComplete(section);
    return;
  }

  const ready = await waitForSplideReady(splideEl);
  if (!ready) {
    revealSlidesStatic(splideEl);
    notifyAppearComplete(section);
    return;
  }

  const slides = getVisibleSlides(splideEl);
  if (slides.length === 0) {
    revealSlidesStatic(splideEl);
    notifyAppearComplete(section);
    return;
  }

  const active = splideEl.querySelector('.splide__slide.is-active');
  let activeIndex =
    active instanceof HTMLElement ? slides.indexOf(active) : -1;
  if (activeIndex < 0) {
    activeIndex = Math.floor(slides.length / 2);
  }

  const cfg = getSectionAppear(section);
  let maxTier = 0;
  slides.forEach((slide, index) => {
    // Radial stagger: the active/centre slide is tier 0, neighbours fan out
    // symmetrically (±1, ±2, …) so the reveal grows from the middle outward.
    const tier = Math.abs(index - activeIndex);
    maxTier = Math.max(maxTier, tier);
    const delay = tier * cfg.tierDelayS;
    animate(slide, { opacity: [0, 1] }, { duration: cfg.opacityDurationS, ease: EASE_OPACITY, delay });
    animate(slide, { y: [120, 0] }, { duration: cfg.durationS, ease: cfg.easeInView, delay });
  });

  const settleMs =
    Math.ceil((maxTier * cfg.tierDelayS + Math.max(cfg.durationS, cfg.opacityDurationS)) * 1000) + 100;
  window.setTimeout(() => {
    revealSlidesStatic(splideEl);
    notifyAppearComplete(section);
  }, settleMs);
}

/**
 * @param {HTMLElement} section
 */
function initSection(section) {
  if (section.dataset.cardsCarouselAppearBound === 'true') return;
  const splideEl = section.querySelector(SPLIDE_SELECTOR);
  if (!splideEl || !(splideEl instanceof HTMLElement)) return;

  section.dataset.cardsCarouselAppearBound = 'true';

  /** @type {() => void} */
  let stopInView = () => {};

  cardsCarouselAppearCleanups.set(section, () => {
    stopInView();
    delete section.dataset.cardsCarouselAppearBound;
  });

  if (prefersReducedMotion()) {
    if (section.dataset.spotlight === 'true') {
      void waitForTrackHeightLocked(section).then(() => notifyAppearComplete(section));
    }
    return;
  }

  import('@theme/motion')
    .then((motion) => {
      if (section.dataset.cardsCarouselAppearBound !== 'true') return;
      const { inView, animate } = motion;
      stopInView = inView(
        section,
        () => {
          void runCarouselAppear(section, splideEl, animate);
        },
        {
          // "some" (any part visible) so the reveal still fires on mobile, where
          // the carousel section is taller than the viewport and a fractional
          // amount (e.g. 0.75) can never be satisfied — which left every slide
          // stuck at opacity:0. Matches 288-stagger-appear / 288-split-text-appear.
          amount: "some",
        }
      );
    })
    .catch(() => {
      if (section.dataset.cardsCarouselAppearBound !== 'true') return;
      revealSlidesStatic(splideEl);
    });
}

/**
 * @param {HTMLElement} section
 */
function destroySection(section) {
  const fn = cardsCarouselAppearCleanups.get(section);
  if (typeof fn === 'function') fn();
  cardsCarouselAppearCleanups.delete(section);
}

function run() {
  document.querySelectorAll(SECTION_SELECTOR).forEach((el) => {
    if (el instanceof HTMLElement) initSection(el);
  });
}

whenReady(run);

document.addEventListener('shopify:section:load', (e) => {
  const t = e.target;
  if (!(t instanceof Element)) return;
  const sections = t.matches(SECTION_SELECTOR)
    ? [t]
    : [...t.querySelectorAll(SECTION_SELECTOR)];
  sections.forEach((el) => {
    if (el instanceof HTMLElement) initSection(el);
  });
});

document.addEventListener('shopify:section:unload', (e) => {
  const t = e.target;
  if (!(t instanceof Element)) return;
  const sections = t.matches(SECTION_SELECTOR)
    ? [t]
    : [...t.querySelectorAll(SECTION_SELECTOR)];
  sections.forEach((el) => {
    if (el instanceof HTMLElement) destroySection(el);
  });
});
